import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';

const card = { background: colors.cardBg, boxShadow: colors.shadowCard, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.25rem', marginBottom: '1rem' };
const inputStyle = { width: '100%', padding: '0.6rem 0.75rem', background: colors.cardBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, color: colors.textPrimary, fontFamily, fontSize: '0.9rem', boxSizing: 'border-box' };
const btnPrimary = { padding: '0.45rem 1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.85rem' };
const btnDanger = { padding: '0.45rem 0.75rem', background: 'rgba(220,53,69,0.15)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.8rem' };
const btnGhost = { padding: '0.45rem 0.75rem', background: 'none', border: `1px solid ${colors.goldMuted}`, color: colors.textMuted, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.8rem' };

function RuleList({ title, category, items, onAdd, onUpdate, onDelete, onMove }) {
  const [newText, setNewText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    try {
      await onAdd(newText.trim(), category);
      setNewText('');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (id) => {
    if (!editText.trim()) return;
    setSaving(true);
    try {
      await onUpdate(id, { content: editText.trim() });
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={card}>
      <h3 style={{ color: colors.textPrimary, margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 700 }}>{title}</h3>

      {items.length === 0 && (
        <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>No {category === 'rule' ? 'rules' : 'FAQ entries'} yet.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
        {items.map((item, idx) => (
          <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', background: colors.sectionBg, borderRadius: 4, padding: '0.5rem 0.75rem' }}>
            <span style={{ color: colors.gold, fontSize: '0.85rem', minWidth: 22, fontWeight: 700, paddingTop: 2 }}>{idx + 1}.</span>

            {editingId === item.id ? (
              <div style={{ flex: 1, display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                <input
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                  onKeyDown={e => e.key === 'Enter' && handleSaveEdit(item.id)}
                  autoFocus
                />
                <button onClick={() => handleSaveEdit(item.id)} disabled={saving} style={btnPrimary}>Save</button>
                <button onClick={() => setEditingId(null)} style={btnGhost}>Cancel</button>
              </div>
            ) : (
              <>
                <span style={{ flex: 1, color: colors.textPrimary, fontSize: '0.9rem', lineHeight: 1.5 }}>{item.content}</span>
                <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
                  {idx > 0 && (
                    <button onClick={() => onMove(item.id, items[idx - 1].id)} style={btnGhost} title="Move up">↑</button>
                  )}
                  {idx < items.length - 1 && (
                    <button onClick={() => onMove(item.id, items[idx + 1].id)} style={btnGhost} title="Move down">↓</button>
                  )}
                  <button onClick={() => { setEditingId(item.id); setEditText(item.content); }} style={btnGhost}>Edit</button>
                  <button onClick={() => onDelete(item.id)} style={btnDanger}>Delete</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          value={newText}
          onChange={e => setNewText(e.target.value)}
          placeholder={`Add new ${category === 'rule' ? 'rule' : 'FAQ entry'}…`}
          style={{ ...inputStyle, flex: 1 }}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
        />
        <button onClick={handleAdd} disabled={saving || !newText.trim()} style={btnPrimary}>
          {saving ? '…' : 'Add'}
        </button>
      </div>
    </div>
  );
}

export default function RulesManagement() {
  const [rules, setRules] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.getRules();
      setRules(data.rules || []);
      setFaqs(data.faqs || []);
    } catch (e) {
      setError('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (content, category) => {
    await api.createRule({ content, category });
    await load();
  };

  const handleUpdate = async (id, updates) => {
    await api.updateRule(id, updates);
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await api.deleteRule(id);
    await load();
  };

  const handleMove = async (aId, bId, list) => {
    const aItem = list.find(x => x.id === aId);
    const bItem = list.find(x => x.id === bId);
    if (!aItem || !bItem) return;
    await api.reorderRules([
      { id: aId, order: bItem.order },
      { id: bId, order: aItem.order },
    ]);
    await load();
  };

  if (loading) return <div style={{ color: colors.textMuted, padding: '2rem', textAlign: 'center' }}>Loading…</div>;

  return (
    <div>
      <h2 style={{ color: colors.textPrimary, margin: '0 0 1.5rem 0', fontSize: '1.1rem', fontWeight: 700 }}>Rules &amp; FAQ Management</h2>
      {error && <div style={{ color: colors.negative, marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}

      <RuleList
        title="House Rules"
        category="rule"
        items={rules}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onMove={(a, b) => handleMove(a, b, rules)}
      />

      <RuleList
        title="Frequently Asked Questions"
        category="faq"
        items={faqs}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onMove={(a, b) => handleMove(a, b, faqs)}
      />
    </div>
  );
}
